import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Modal, Form } from "react-bootstrap";
import { Button } from "bootstrap-4-react/lib/components";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import InputCalculator from "../common/InputCalculator";
import { normalizeCalculatorValue, hasUnevaluatedExpression } from "../common/calculatorValue";
import { importDropshipProduct, getDropshipMarginConfig } from "../../store/actions/dropship.action";

const SHOW_MARGIN_KEY = "DROPSHIP_SHOW_MARGIN_TO_DROPSHIPPER";

export default function ImportModal({ show, onHide, product, onSuccess }) {
    const dispatch = useDispatch();
    const { marginConfigs } = useSelector((state) => state.dropshipReducer);
    const appConfigurations = useSelector((state) => state?.auth?.configurations) || [];
    const showMarginToDropshipper = String(
        appConfigurations.find((c) => c.configKey === SHOW_MARGIN_KEY)?.configValue ?? "false"
    ).toLowerCase() === "true";

    const [sellingPrice, setSellingPrice] = useState("");
    const [reservedQuantity, setReservedQuantity] = useState("");
    const [reservationDays, setReservationDays] = useState("30");
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (show) dispatch(getDropshipMarginConfig({}));
    }, [show]);

    const globalMargin = marginConfigs.find((c) => c.scope === "Global") || { flatAmount: 0, percentAmount: 0 };
    const marginFlat = Number.parseFloat(globalMargin.flatAmount) || 0;
    const marginPercent = Number.parseFloat(globalMargin.percentAmount) || 0;

    const supplierWholesale = product?.wholesalePrice || 0;
    const availableQty = product?.availableQuantity || 0;
    const selling = Number.parseFloat(sellingPrice) || 0;
    const reserved = Number.parseInt(reservedQuantity, 10) || 0;
    // Buyer's effective wholesale = supplier price + 3PL margin (flat + percent),
    // shown to the dropshipper as a single "Wholesale Price" line. The 3PL cut
    // is intentionally hidden — the dropshipper shouldn't need to think about it.
    const platformMarginPerUnit = marginFlat + (supplierWholesale * marginPercent / 100);
    const costPerUnit = supplierWholesale + platformMarginPerUnit;
    const margin = selling - costPerUnit;
    const totalCommitment = costPerUnit * reserved;

    const handleSubmit = async () => {
        if (!sellingPrice || selling <= 0 || hasUnevaluatedExpression(sellingPrice)) {
            toast.error("Please enter a valid selling price.");
            return;
        }
        if (!reservedQuantity || reserved <= 0) {
            toast.error("Please enter the quantity to reserve.");
            return;
        }
        if (reserved > availableQty) {
            toast.error(`Only ${availableQty} units are available from this supplier.`);
            return;
        }
        if (!reservationDays || Number.parseInt(reservationDays, 10) <= 0) {
            toast.error("Please enter a valid reservation period in days.");
            return;
        }
        if (!acceptTerms) {
            toast.error("Please accept the terms to import this product.");
            return;
        }
        if (margin < 0) {
            toast.warning("Your selling price is below wholesale price. You will lose money on each sale.");
        }

        setSubmitting(true);
        try {
            const result = await importDropshipProduct({
                dropshipProductId: product.id,
                sellingPrice: selling,
                reservedQuantity: reserved,
                reservationDays: Number.parseInt(reservationDays, 10),
            });
            const response = result.data?.EntityList?.[0];
            if (response?.success) {
                toast.success(`Imported. Reserved ${reserved} units for ${reservationDays} days.`);
                onSuccess?.();
                onHide();
            } else {
                toast.error(response?.message || "Failed to import product.");
            }
        } catch (err) {
            toast.error("Failed to import product.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered size="md">
            <Modal.Header closeButton>
                <Modal.Title>Import Product</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* Product Info */}
                <div className="d-flex align-items-center gap-3 mb-3">
                    {product.imageUrl ? (
                        <img src={product.imageUrl} alt="" className="dropship-product-thumb" style={{ width: 50, height: 50 }} />
                    ) : (
                        <div className="dropship-no-image" style={{ width: 50, height: 50 }}>
                            <i className="fa fa-image" />
                        </div>
                    )}
                    <div>
                        <div style={{ fontWeight: 600 }}>{product.productName}</div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                            {product.productSKU} | Verified Supplier
                        </div>
                    </div>
                </div>

                {/* Terms */}
                <div className="terms-section">
                    <div className="term-row">
                        <span className="term-label">Wholesale Price</span>
                        <span className="term-value">${costPerUnit.toFixed(2)}</span>
                    </div>
                    <div className="term-row">
                        <span className="term-label">Available</span>
                        <span className="term-value" style={{ color: availableQty > 0 ? "#28a745" : "#dc3545" }}>
                            {availableQty} units
                        </span>
                    </div>
                    <div className="term-row">
                        <span className="term-label">Shipping Paid By</span>
                        <span className="term-value">{product.shippingPaidBy}</span>
                    </div>
                    <div className="term-row">
                        <span className="term-label">Fulfillment Time</span>
                        <span className="term-value">{product.fulfillmentDays} days</span>
                    </div>
                    {product.returnPolicy && (
                        <div className="term-row">
                            <span className="term-label">Return Policy</span>
                            <span className="term-value">{product.returnPolicy}</span>
                        </div>
                    )}
                </div>

                {/* Reservation */}
                <Form.Group className="mb-3 mt-3">
                    <Form.Label>Quantity to Reserve</Form.Label>
                    <Form.Control
                        type="number"
                        min="1"
                        max={availableQty}
                        placeholder={`Up to ${availableQty} units`}
                        value={reservedQuantity}
                        onChange={(e) => setReservedQuantity(e.target.value)}
                    />
                    <Form.Text className="text-muted">
                        Reserved units are held for you. Unsold units return to the supplier when reservation expires.
                    </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Reservation Period (Days)</Form.Label>
                    <Form.Select value={reservationDays} onChange={(e) => setReservationDays(e.target.value)}>
                        <option value="7">7 days</option>
                        <option value="14">14 days</option>
                        <option value="30">30 days (default)</option>
                        <option value="60">60 days</option>
                        <option value="90">90 days</option>
                    </Form.Select>
                </Form.Group>

                {/* Selling Price */}
                <Form.Group className="mb-3">
                    <Form.Label>Your Selling Price ($)</Form.Label>
                    <InputCalculator
                        inputMode="decimal"
                        maxLength={50}
                        placeholder="Enter your selling price"
                        value={sellingPrice}
                        onChange={(e) => {
                            const value = normalizeCalculatorValue(e);
                            if (value !== null) setSellingPrice(value);
                        }}
                    />
                </Form.Group>

                {/* Margin Preview — 3PL cut hidden by default (baked into Wholesale Price);
                    super-admin can toggle DROPSHIP_SHOW_MARGIN_TO_DROPSHIPPER on the
                    Dropshipping Margin Configuration page to expose the breakdown. */}
                {selling > 0 && (
                    <div className="margin-preview">
                        <div className="row">
                            <span>Selling Price (per unit)</span>
                            <span>${selling.toFixed(2)}</span>
                        </div>
                        {showMarginToDropshipper ? (
                            <>
                                <div className="row">
                                    <span>Supplier Wholesale (per unit)</span>
                                    <span>-${supplierWholesale.toFixed(2)}</span>
                                </div>
                                <div className="row">
                                    <span>Platform Margin (per unit)</span>
                                    <span>-${platformMarginPerUnit.toFixed(2)}</span>
                                </div>
                                <div className="row" style={{ borderTop: "1px solid #eee", paddingTop: 4, marginTop: 4 }}>
                                    <span>Total Cost (per unit)</span>
                                    <span>-${costPerUnit.toFixed(2)}</span>
                                </div>
                            </>
                        ) : (
                            <div className="row">
                                <span>Wholesale Price (per unit)</span>
                                <span>-${costPerUnit.toFixed(2)}</span>
                            </div>
                        )}
                        <div className={`row total ${margin >= 0 ? "margin-positive" : "margin-negative"}`}>
                            <span>Your Margin per Unit</span>
                            <span>${margin.toFixed(2)}</span>
                        </div>
                        {reserved > 0 && (
                            <div className="row" style={{ marginTop: 6, color: "#666" }}>
                                <span>Total potential profit ({reserved} units)</span>
                                <span style={{ fontWeight: 600 }}>${(margin * reserved).toFixed(2)}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Accept Terms — platform-standard Bootstrap form-check pattern */}
                <div className="form-check mt-3">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        id="dropshipImportAcceptTerms"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="dropshipImportAcceptTerms">
                        I accept the supplier's terms and reserve {reserved || 0} units (total commitment: ${totalCommitment.toFixed(2)}) for {reservationDays} days
                    </label>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button className="cus-primary-transparent-btn" onClick={onHide} disabled={submitting}>
                    Cancel
                </Button>
                <Button
                    className="cus-seconday-bg-btn"
                    onClick={handleSubmit}
                    disabled={submitting || !acceptTerms}
                >
                    {submitting ? "Importing..." : "Import & Reserve"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

ImportModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    product: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        productName: PropTypes.string,
        productSKU: PropTypes.string,
        supplierName: PropTypes.string,
        imageUrl: PropTypes.string,
        wholesalePrice: PropTypes.number,
        availableQuantity: PropTypes.number,
        shippingPaidBy: PropTypes.string,
        fulfillmentDays: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        returnPolicy: PropTypes.string,
    }).isRequired,
};

ImportModal.defaultProps = {
    onSuccess: undefined,
};
